import { useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, RefreshCw, Swords } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatAIText } from "@/lib/aiFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "insights", label: "Daily Insights", icon: Sparkles, get: "/ai/insights", post: "/ai/insights/generate", field: "insight" },
  { key: "review", label: "Brand vs Competition", icon: Swords, get: "/ai/brand-review", post: "/ai/brand-review/generate", field: "review" },
];

export const AIInsights = () => {
  const [tab, setTab] = useState("insights");
  const [data, setData] = useState({ insights: null, review: null });
  const [aiEnabled, setAiEnabled] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const active = TABS.find((t) => t.key === tab);
  const current = data[tab];

  useEffect(() => {
    TABS.forEach(async (t) => {
      try {
        const { data: res } = await axios.get(`${API}${t.get}`);
        setData((prev) => ({ ...prev, [t.key]: res[t.field] }));
        setAiEnabled(res.ai_enabled);
      } catch (e) {
        console.error(`Failed to fetch ${t.key}:`, e);
      }
    });
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data: res } = await axios.post(`${API}${active.post}`);
      setData((prev) => ({ ...prev, [tab]: res[active.field] }));
      setExpanded(true);
      toast.success(`${active.label} generated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate");
    } finally {
      setGenerating(false);
    }
  };

  if (!aiEnabled) return null;

  return (
    <div
      data-testid="ai-insights-card"
      className="bg-gradient-to-br from-[#1a1814] to-[#2d2820] text-white rounded-xl border border-[#3d3830] shadow-lg overflow-hidden"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#f0a500]/20 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-[#f0a500]" />
          </div>
          <div>
            <p className="text-sm font-extrabold tracking-tight">AI Analyst</p>
            <p className="text-[10px] text-white/50">
              {current
                ? `Powered by Claude · ${new Date(current.generated_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`
                : "Powered by Claude Haiku"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Tabs */}
          <div className="flex rounded-lg bg-white/5 border border-white/10 p-0.5">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`ai-tab-${t.key}`}
                onClick={() => { setTab(t.key); setExpanded(true); }}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors",
                  tab === t.key ? "bg-[#f0a500] text-[#1a1200]" : "text-white/60 hover:text-white"
                )}
              >
                <t.icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>

          <Button
            data-testid="generate-insights-btn"
            size="sm"
            onClick={generate}
            disabled={generating}
            className="bg-white/10 hover:bg-white/20 text-white font-bold border border-white/15"
          >
            {generating ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                {current ? "Refresh" : "Generate"}
              </>
            )}
          </Button>
        </div>
      </div>

      {expanded && current && (
        <div
          data-testid="ai-insights-content"
          className="px-5 pb-5 pt-3 text-[13px] leading-relaxed text-white/85 space-y-0.5 border-t border-white/10"
          dangerouslySetInnerHTML={{ __html: formatAIText(current.content) }}
        />
      )}

      {expanded && !current && !generating && (
        <div className="px-5 pb-5 pt-3 text-[13px] text-white/50 border-t border-white/10">
          {tab === "insights"
            ? "No insights yet — click Generate to have Claude analyze today's prices, BSR moves and markdowns."
            : "No review yet — click Generate for Claude's Butterfly vs competitors verdict over the past day and week."}
        </div>
      )}
    </div>
  );
};
